import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Navbar, Footer } from '../../components/Layout/Layout';
import { ServiceBadgesCompact, ServiceLegend } from '../../components/ServiceBadges/ServiceBadges';
import { AddressAutocomplete } from '../../components/AddressAutocomplete/AddressAutocomplete';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Search, Loader2, MapPin, Users, Clock, Star, Send, CheckSquare, Square, Shield, Navigation } from 'lucide-react';
import useCanonical from '../../hooks/useCanonical';

const SearchPage = () => {
  // Set canonical URL
  useCanonical('https://servio.legal/search');
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedServers, setSelectedServers] = useState([]);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchLocation, setSearchLocation] = useState(null);  // Store location from URL or autocomplete
  const [displayPlaceName, setDisplayPlaceName] = useState(''); // Display name for search header
  
  const [quoteForm, setQuoteForm] = useState({
    client_name: '', client_company: '', client_email: '', client_phone: '',
    service_street: '', service_city: '', service_state: '', service_zip: '',
    document_type: '', urgency: 'standard', num_serves: 1, additional_details: ''
  });

  const [filters, setFilters] = useState({
    min_trust_score: searchParams.get('min_trust_score') || '',
    verified_only: searchParams.get('verified_only') === 'true',
    max_distance: searchParams.get('max_distance') || '',
    sort_by: searchParams.get('sort_by') || 'trust_score'
  });

  // Parse initial search from URL params
  useEffect(() => {
    const lat = searchParams.get('lat');
    const lng = searchParams.get('lng');
    const placeName = searchParams.get('place_name');
    const placeType = searchParams.get('place_type');
    const state = searchParams.get('state');
    const county = searchParams.get('county');
    const city = searchParams.get('city');
    const zipCode = searchParams.get('zip_code');
    
    // Check if we have any search parameters
    const hasParams = lat || state || county || city || zipCode;
    
    if (hasParams) {
      // Build location object from URL params
      const locationFromUrl = {
        coordinates: lat && lng ? [parseFloat(lng), parseFloat(lat)] : null,
        name: placeName || '',
        placeType: placeType || 'address',
        stateCode: state || null,
        county: county || null,
        city: city || null,
        shortName: zipCode || city || county || state || ''
      };
      
      setSearchLocation(locationFromUrl);
      setDisplayPlaceName(placeName || city || county || state || zipCode || '');
      setHasSearched(true);
      performSearchFromParams();
    }
  }, []);

  const performSearchFromParams = () => {
    setLoading(true);
    
    // Build search filters from URL params
    const searchFilters = {
      lat: searchParams.get('lat') ? parseFloat(searchParams.get('lat')) : undefined,
      lng: searchParams.get('lng') ? parseFloat(searchParams.get('lng')) : undefined,
      place_name: searchParams.get('place_name') || undefined,
      place_type: searchParams.get('place_type') || undefined,
      state: searchParams.get('state') || undefined,
      county: searchParams.get('county') || undefined,
      city: searchParams.get('city') || undefined,
      zip_code: searchParams.get('zip_code') || undefined,
      min_trust_score: filters.min_trust_score || undefined,
      verified_only: filters.verified_only === true ? true : undefined,
      max_distance: filters.max_distance || undefined,
      sort_by: filters.sort_by,
      page,
      limit: 20
    };
    
    api.searchServers(searchFilters).then(res => {
      setResults(res.data.results);
      setTotal(res.data.total);
      setSelectedServers([]);
    }).catch(() => {
      setResults([]);
      setTotal(0);
    }).finally(() => setLoading(false));
  };

  const handleLocationSelect = (location) => {
    setSearchLocation(location);
    if (location) {
      setDisplayPlaceName(location.name);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    
    if (!searchLocation) {
      toast.error('Please select a location to search');
      return;
    }
    
    setHasSearched(true);
    setPage(1);
    
    // Build URL params
    const params = new URLSearchParams();
    
    if (searchLocation.coordinates) {
      params.set('lng', searchLocation.coordinates[0]);
      params.set('lat', searchLocation.coordinates[1]);
    }
    
    if (searchLocation.name) {
      params.set('place_name', searchLocation.name);
    }
    if (searchLocation.placeType) {
      params.set('place_type', searchLocation.placeType);
    }
    
    // Add specific filters
    if (searchLocation.stateCode) {
      params.set('state', searchLocation.stateCode);
    }
    if (searchLocation.county) {
      params.set('county', searchLocation.county);
    }
    if (searchLocation.city) {
      params.set('city', searchLocation.city);
    }
    if (searchLocation.placeType === 'zip') {
      params.set('zip_code', searchLocation.shortName);
    }
    
    setSearchParams(params);
    
    // Perform search
    setLoading(true);
    
    const searchFilters = {
      lat: searchLocation.coordinates ? searchLocation.coordinates[1] : undefined,
      lng: searchLocation.coordinates ? searchLocation.coordinates[0] : undefined,
      place_name: searchLocation.name || undefined,
      place_type: searchLocation.placeType || undefined,
      state: searchLocation.stateCode || undefined,
      county: searchLocation.county || undefined,
      city: searchLocation.placeType === 'city' ? searchLocation.shortName : searchLocation.city || undefined,
      zip_code: searchLocation.placeType === 'zip' ? searchLocation.shortName : undefined,
      min_trust_score: filters.min_trust_score || undefined,
      verified_only: filters.verified_only === true ? true : undefined,
      max_distance: filters.max_distance || undefined,
      sort_by: filters.sort_by,
      page: 1,
      limit: 20
    };
    
    api.searchServers(searchFilters).then(res => {
      setResults(res.data.results);
      setTotal(res.data.total);
      setSelectedServers([]);
    }).catch(() => {
      setResults([]);
      setTotal(0);
    }).finally(() => setLoading(false));
  };

  const handleFilterChange = (key, value) => {
    // For checkboxes, keep boolean values; for selects, convert 'all'/'any' to empty
    const actualValue = (typeof value === 'boolean') ? value : (value === 'all' || value === 'any' ? '' : value);
    setFilters(prev => ({ ...prev, [key]: actualValue }));
  };
  
  // Trigger search when filters change (if already searched)
  useEffect(() => {
    if (hasSearched) {
      setPage(1);
      performSearchFromParams();
    }
  }, [filters.verified_only, filters.min_trust_score, filters.max_distance, filters.sort_by]);

  const toggleServerSelection = (serverId) => {
    setSelectedServers(prev => prev.includes(serverId) ? prev.filter(id => id !== serverId) : [...prev, serverId]);
  };
  
  const selectAllServers = () => {
    setSelectedServers(selectedServers.length === results.length ? [] : results.map(s => s.id));
  };
  
  const updateQuoteField = (field, value) => setQuoteForm(prev => ({ ...prev, [field]: value }));

  // ---- Empty-state helpers ----
  const hasActiveFilters = !!(filters.min_trust_score || filters.verified_only || filters.max_distance);
  const isNarrowerThanState = !!(searchLocation?.stateCode && (searchLocation.city || searchLocation.county || searchLocation.placeType === 'zip' || searchLocation.placeType === 'address' || searchLocation.coordinates));

  const clearFiltersAndSearch = () => {
    setFilters(prev => ({ ...prev, min_trust_score: '', verified_only: false, max_distance: '' }));
    // Strip filter params from the URL so the shareable link matches the cleared state.
    const params = new URLSearchParams(searchParams);
    ['min_trust_score', 'verified_only', 'max_distance'].forEach(k => params.delete(k));
    setSearchParams(params);
    // The filters useEffect re-runs the search automatically for the same location.
    toast.success('Filters cleared');
  };

  const searchWholeState = () => {
    if (!searchLocation?.stateCode) return;
    const code = searchLocation.stateCode;
    const stateLoc = { coordinates: null, name: code, placeType: 'region', stateCode: code, county: null, city: null, shortName: code };
    setSearchLocation(stateLoc);
    setDisplayPlaceName(code);
    const params = new URLSearchParams();
    params.set('place_name', code);
    params.set('place_type', 'region');
    params.set('state', code);
    setSearchParams(params);
    setPage(1);
    setLoading(true);
    api.searchServers({ state: code, sort_by: filters.sort_by, page: 1, limit: 20 })
      .then(res => { setResults(res.data.results); setTotal(res.data.total); setSelectedServers([]); })
      .catch(() => { setResults([]); setTotal(0); })
      .finally(() => setLoading(false));
  };
  
  const handleBroadcastQuote = async (e) => {
    e.preventDefault();
    if (selectedServers.length === 0) { toast.error('Please select at least one server'); return; }
    setSubmitting(true);
    try {
      await Promise.all(selectedServers.map(serverId => 
        api.requestQuote({
          server_id: serverId, client_name: quoteForm.client_name, client_company: quoteForm.client_company,
          client_email: quoteForm.client_email, client_phone: quoteForm.client_phone,
          service_street: quoteForm.service_street, service_city: quoteForm.service_city,
          service_state: quoteForm.service_state, service_zip: quoteForm.service_zip,
          document_type: quoteForm.document_type, urgency: quoteForm.urgency,
          num_serves: parseInt(quoteForm.num_serves) || 1, additional_details: quoteForm.additional_details
        })
      ));
      toast.success(`Quote sent to ${selectedServers.length} server${selectedServers.length > 1 ? 's' : ''}!`);
      setQuoteModalOpen(false);
      setSelectedServers([]);
      setQuoteForm({ client_name: '', client_company: '', client_email: '', client_phone: '', service_street: '', service_city: '', service_state: '', service_zip: '', document_type: '', urgency: 'standard', num_serves: 1, additional_details: '' });
    } catch (error) {
      toast.error('Failed to send quote requests');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-[hsl(220,40%,5%)] text-slate-900 dark:text-[hsl(220,10%,96%)]">
      <Helmet>
        <title>Find Process Servers | Search by Location | Servio</title>
        <meta name="description" content="Search for verified process servers by state, county, city, or ZIP code. Compare Servio Scores, view coverage areas, and request quotes from trusted professionals." />
        <link rel="canonical" href="https://servio.legal/search" />
      </Helmet>
      <Navbar />
      <main className="flex-1">
        {/* Search Header — Modern hero */}
        <section className="relative overflow-hidden isolate py-14 lg:py-20" data-testid="search-hero">
          <div className="absolute inset-0 dark:hidden pointer-events-none" style={{
            background:
              'radial-gradient(800px 360px at 20% 0%, hsl(226 100% 55% / 0.08), transparent 60%),' +
              'radial-gradient(800px 400px at 95% 10%, hsl(270 95% 60% / 0.06), transparent 60%)',
          }} />
          <div className="absolute inset-0 hidden dark:block pointer-events-none" style={{
            background: 'radial-gradient(60% 50% at 50% 0%, hsl(226 100% 55% / 0.14), transparent 70%)',
          }} />
          <div className="absolute inset-0 dark:hidden opacity-[0.5] pointer-events-none" style={{
            backgroundImage: 'linear-gradient(hsl(220 16% 92%) 1px, transparent 1px), linear-gradient(90deg, hsl(220 16% 92%) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black, transparent 75%)',
          }} />

          <div className="relative z-10 max-w-[1280px] mx-auto px-5 sm:px-8">
            <div className="max-w-3xl mx-auto text-center mb-8">
              <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] mb-4">◢ DIRECTORY</div>
              <h1 className="font-bold leading-[0.95] tracking-[-0.04em] mx-auto max-w-3xl" style={{ fontSize: 'clamp(2rem, 4.4vw, 3.6rem)' }}>
                Find a server. <span className="italic font-normal text-slate-500 dark:text-[hsl(220,12%,60%)]" style={{ fontFamily: '"Instrument Serif"' }}>Anywhere.</span>
              </h1>
              <p className="mt-4 text-base text-slate-600 dark:text-[hsl(220,12%,72%)] leading-relaxed">Any address, city, county, ZIP, or state. Verified Servio Scores, instant quotes, one tap to hire.</p>
            </div>

            {/* Search Form - Unified Address Search */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto" data-testid="search-form">
              <div className="group relative rounded-2xl bg-white dark:bg-[hsl(220,30%,8%)] backdrop-blur-xl border border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 transition-all p-2 flex items-center gap-2 shadow-[0_8px_24px_-8px_rgba(15,23,42,0.10)] dark:shadow-[0_24px_60px_-16px_rgba(30,91,246,0.45)]">
                <div className="flex-1 min-w-0">
                  <AddressAutocomplete
                    onSelect={handleLocationSelect}
                    placeholder="Search any address, city, county, ZIP, or state…"
                  />
                </div>
                <button type="submit" className="h-12 px-6 rounded-xl bg-[hsl(226,100%,55%)] hover:bg-[hsl(226,100%,62%)] text-white font-semibold tracking-tight text-sm flex items-center gap-2 whitespace-nowrap shadow-[0_10px_30px_-8px_hsl(226,100%,55%)] transition-colors" data-testid="search-btn">
                  <Search className="h-4 w-4" />
                  Search
                </button>
              </div>
            </form>
          </div>
        </section>

        {/* Results Section */}
        {!hasSearched ? (
          /* Empty State - No Search Yet */
          <section className="py-16">
            <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
              <div className="max-w-md mx-auto text-center">
                <div className="w-20 h-20 rounded-2xl bg-slate-100 dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 flex items-center justify-center mx-auto mb-6">
                  <Search className="h-9 w-9 text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)]" />
                </div>
                <h2 className="text-xl font-semibold tracking-tight mb-2">Start your search</h2>
                <p className="text-sm text-slate-600 dark:text-[hsl(220,12%,72%)] leading-relaxed">
                  Type an address, city, county, ZIP, or state above to find verified process servers in seconds.
                </p>
              </div>
            </div>
          </section>
        ) : (
          /* Results - After Search */
          <section className="py-6">
            <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
              {/* Filters Row */}
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-4 border-b border-slate-200 dark:border-white/[0.06]">
              <div className="flex items-center gap-2">
                <span className="text-slate-500">Sort:</span>
                <Select value={filters.sort_by} onValueChange={(v) => handleFilterChange('sort_by', v)}>
                  <SelectTrigger className="w-36 bg-slate-800/60 border-slate-700 text-white h-8"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="trust_score" className="text-slate-300">Servio Score</SelectItem>
                    <SelectItem value="distance" className="text-slate-300">Distance</SelectItem>
                    <SelectItem value="price" className="text-slate-300">Price</SelectItem>
                    <SelectItem value="reviews" className="text-slate-300">Reviews</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500">Within:</span>
                <Select value={filters.max_distance} onValueChange={(v) => handleFilterChange('max_distance', v)}>
                  <SelectTrigger className="w-24 bg-slate-800/60 border-slate-700 text-white h-8"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="any" className="text-slate-300">Any</SelectItem>
                    <SelectItem value="25" className="text-slate-300">25 mi</SelectItem>
                    <SelectItem value="50" className="text-slate-300">50 mi</SelectItem>
                    <SelectItem value="100" className="text-slate-300">100 mi</SelectItem>
                    <SelectItem value="200" className="text-slate-300">200 mi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500">Min Score:</span>
                <Select value={filters.min_trust_score} onValueChange={(v) => handleFilterChange('min_trust_score', v)}>
                  <SelectTrigger className="w-20 bg-slate-800/60 border-slate-700 text-white h-8"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="any" className="text-slate-300">Any</SelectItem>
                    <SelectItem value="90" className="text-slate-300">90+</SelectItem>
                    <SelectItem value="80" className="text-slate-300">80+</SelectItem>
                    <SelectItem value="70" className="text-slate-300">70+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={filters.verified_only} onCheckedChange={(v) => handleFilterChange('verified_only', v)} className="border-slate-600 data-[state=checked]:bg-teal-500" />
                <span className="text-slate-400">Verified Only</span>
              </label>
              <div className="flex-1" />
              {results.length > 0 && (
                <>
                  <button onClick={selectAllServers} className="flex items-center gap-2 text-slate-400 hover:text-white">
                    {selectedServers.length === results.length ? <CheckSquare className="h-4 w-4 text-teal-400" /> : <Square className="h-4 w-4" />}
                    {selectedServers.length === results.length ? 'Deselect All' : 'Select All'}
                  </button>
                  {selectedServers.length > 0 && (
                    <Dialog open={quoteModalOpen} onOpenChange={setQuoteModalOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="btn-accent"><Send className="h-4 w-4 mr-2" />Quote {selectedServers.length}</Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
                        <DialogHeader><DialogTitle className="text-white">Broadcast Quote Request</DialogTitle></DialogHeader>
                        <p className="text-sm text-slate-400 mb-4">Sending to {selectedServers.length} server{selectedServers.length > 1 ? 's' : ''}.</p>
                        <form onSubmit={handleBroadcastQuote} className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div><Label htmlFor="bq-client-name" className="text-slate-300">Your Name *</Label><Input id="bq-client-name" name="client_name" required value={quoteForm.client_name} onChange={(e) => updateQuoteField('client_name', e.target.value)} className="bg-slate-800 border-slate-700 text-white" autoComplete="name" /></div>
                            <div><Label htmlFor="bq-client-company" className="text-slate-300">Company</Label><Input id="bq-client-company" name="client_company" value={quoteForm.client_company} onChange={(e) => updateQuoteField('client_company', e.target.value)} className="bg-slate-800 border-slate-700 text-white" autoComplete="organization" /></div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div><Label htmlFor="bq-client-email" className="text-slate-300">Email *</Label><Input id="bq-client-email" name="client_email" type="email" required value={quoteForm.client_email} onChange={(e) => updateQuoteField('client_email', e.target.value)} className="bg-slate-800 border-slate-700 text-white" autoComplete="email" /></div>
                            <div><Label htmlFor="bq-client-phone" className="text-slate-300">Phone *</Label><Input id="bq-client-phone" name="client_phone" required value={quoteForm.client_phone} onChange={(e) => updateQuoteField('client_phone', e.target.value)} className="bg-slate-800 border-slate-700 text-white" autoComplete="tel" /></div>
                          </div>
                          <div><Label htmlFor="bq-service-street" className="text-slate-300">Service Address *</Label><Input id="bq-service-street" name="service_street" required placeholder="Street Address" value={quoteForm.service_street} onChange={(e) => updateQuoteField('service_street', e.target.value)} className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" /></div>
                          <div className="grid grid-cols-3 gap-4">
                            <div><Label htmlFor="bq-service-city" className="text-slate-300">City *</Label><Input id="bq-service-city" name="service_city" required value={quoteForm.service_city} onChange={(e) => updateQuoteField('service_city', e.target.value)} className="bg-slate-800 border-slate-700 text-white" /></div>
                            <div><Label htmlFor="bq-service-state" className="text-slate-300">State *</Label><Input id="bq-service-state" name="service_state" required maxLength={2} placeholder="TX" value={quoteForm.service_state} onChange={(e) => updateQuoteField('service_state', e.target.value.toUpperCase())} className="bg-slate-800 border-slate-700 text-white" /></div>
                            <div><Label htmlFor="bq-service-zip" className="text-slate-300">ZIP *</Label><Input id="bq-service-zip" name="service_zip" required value={quoteForm.service_zip} onChange={(e) => updateQuoteField('service_zip', e.target.value)} className="bg-slate-800 border-slate-700 text-white" /></div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div><Label htmlFor="bq-document-type" className="text-slate-300">Document Type *</Label><Input id="bq-document-type" name="document_type" required placeholder="e.g., Subpoena" value={quoteForm.document_type} onChange={(e) => updateQuoteField('document_type', e.target.value)} className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" /></div>
                            <div><Label htmlFor="bq-urgency" className="text-slate-300">Urgency</Label>
                              <Select value={quoteForm.urgency} onValueChange={(v) => updateQuoteField('urgency', v)}>
                                <SelectTrigger id="bq-urgency" className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-700">
                                  <SelectItem value="standard" className="text-slate-300">Standard</SelectItem>
                                  <SelectItem value="rush" className="text-slate-300">Rush</SelectItem>
                                  <SelectItem value="same_day" className="text-slate-300">Same Day</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div><Label htmlFor="bq-additional-details" className="text-slate-300">Additional Details</Label><Textarea id="bq-additional-details" name="additional_details" placeholder="Special instructions..." value={quoteForm.additional_details} onChange={(e) => updateQuoteField('additional_details', e.target.value)} className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" rows={2} /></div>
                          <Button type="submit" className="w-full btn-accent" disabled={submitting}>{submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send Quote Request</Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                </>
              )}
              </div>

              {/* Results Count & Legends */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-slate-400">{loading ? 'Searching...' : `${total} server${total !== 1 ? 's' : ''} found`}</p>
                <div className="flex items-center gap-2">
                  <VerificationLegend />
                  <ServiceLegend compact={true} />
                </div>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-teal-400" /></div>
              ) : results.length > 0 ? (
                <>
                  {/* Desktop Table View */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-xs text-slate-500 border-b border-slate-800">
                          <th className="pb-3 w-10"></th>
                          <th className="pb-3 w-12">Score</th>
                          <th className="pb-3">Server</th>
                          <th className="pb-3">Coverage</th>
                          <th className="pb-3 text-center">Distance</th>
                          <th className="pb-3 text-right">Price</th>
                          <th className="pb-3 text-center">Response</th>
                          <th className="pb-3">Status</th>
                          <th className="pb-3 w-40"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((server) => (
                          <ServerTableRow 
                            key={server.id} 
                            server={server} 
                            selected={selectedServers.includes(server.id)} 
                            onToggle={() => toggleServerSelection(server.id)}
                            searchFilters={{ state: searchLocation?.stateCode, city: searchLocation?.city, zip_code: searchLocation?.placeType === 'zip' ? searchLocation?.shortName : null }}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="md:hidden space-y-4">
                    {results.map((server) => (
                      <ServerMobileCard 
                        key={server.id} 
                        server={server} 
                        selected={selectedServers.includes(server.id)} 
                        onToggle={() => toggleServerSelection(server.id)}
                        searchFilters={{ state: searchLocation?.stateCode, city: searchLocation?.city, zip_code: searchLocation?.placeType === 'zip' ? searchLocation?.shortName : null }}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-16 max-w-xl mx-auto" data-testid="search-empty-state">
                  <div className="mx-auto mb-5 h-16 w-16 rounded-2xl bg-slate-100 dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 flex items-center justify-center">
                    <Users className="h-7 w-7 text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)]" />
                  </div>
                  <h3 className="text-xl font-semibold tracking-tight mb-2 text-slate-900 dark:text-white">
                    No servers found{displayPlaceName ? <> in <span className="text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)]">{displayPlaceName}</span></> : ''}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-[hsl(220,12%,72%)] leading-relaxed mb-7">
                    {hasActiveFilters
                      ? 'Your filters may be too strict. Try loosening them, or broaden your search area.'
                      : 'This area may not have listed servers yet. Try broadening your search or browse the full directory.'}
                  </p>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    {hasActiveFilters && (
                      <Button onClick={clearFiltersAndSearch} className="btn-accent w-full sm:w-auto" data-testid="empty-clear-filters">
                        <Shield className="h-4 w-4 mr-2" /> Clear all filters
                      </Button>
                    )}
                    {isNarrowerThanState && (
                      <Button onClick={searchWholeState} variant="outline" className="w-full sm:w-auto border-slate-300 dark:border-white/15 text-slate-700 dark:text-slate-200" data-testid="empty-search-state">
                        <MapPin className="h-4 w-4 mr-2" /> Search all of {searchLocation.stateCode}
                      </Button>
                    )}
                    <Button asChild variant="outline" className="w-full sm:w-auto border-slate-300 dark:border-white/15 text-slate-700 dark:text-slate-200" data-testid="empty-browse-directory">
                      <Link to="/"><Search className="h-4 w-4 mr-2" /> Browse the directory</Link>
                    </Button>
                  </div>

                  <p className="mt-7 text-xs text-slate-500 dark:text-[hsl(220,12%,55%)]">
                    Tip: edit the search bar above to try a nearby city, county, or ZIP code.
                  </p>
                </div>
              )}

              {total > 20 && (
                <div className="mt-8 flex justify-center gap-2">
                  <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="border-slate-700 text-slate-300">Previous</Button>
                  <span className="flex items-center px-4 text-sm text-slate-400">Page {page} of {Math.ceil(total / 20)}</span>
                  <Button variant="outline" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage(p => p + 1)} className="border-slate-700 text-slate-300">Next</Button>
                </div>
              )}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

// Compact Servio Score display with proper progress ring
const ServioScoreMini = ({ score, size = 'default' }) => {
  const isLarge = size === 'lg';
  const ringSize = isLarge ? 48 : 40;
  const strokeWidth = isLarge ? 4 : 3;
  const radius = (ringSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = ((score || 0) / 100) * circumference;
  
  const getScoreColor = (s) => {
    if (s >= 80) return '#14B8A6';
    if (s >= 60) return '#FBBF24';
    return '#EF4444';
  };
  
  const color = getScoreColor(score);
  
  return (
    <div className="relative inline-flex items-center justify-center flex-shrink-0">
      <svg 
        width={ringSize} 
        height={ringSize} 
        className="transform -rotate-90"
      >
        {/* Background ring */}
        <circle
          cx={ringSize / 2}
          cy={ringSize / 2}
          r={radius}
          fill="none"
          stroke="rgba(51, 65, 85, 0.3)"
          strokeWidth={strokeWidth}
        />
        {/* Progress ring */}
        <circle
          cx={ringSize / 2}
          cy={ringSize / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
        />
      </svg>
      <span 
        className={`absolute font-mono font-bold ${isLarge ? 'text-base' : 'text-sm'}`}
        style={{ color }}
      >
        {score || 0}
      </span>
    </div>
  );
};

// Simple table row
const ServerTableRow = ({ server, selected, onToggle, searchFilters }) => {
  const { id, display_name, company_name, trust_score, default_pricing, coverage_areas, filtered_coverage_areas, verifications, response_guarantee_hours, is_founding_member, review_count, average_rating, distance_miles, additional_services, min_price, max_price, has_call_for_quote, state_certifications } = server;

  // Determine price display
  let priceDisplay;
  if (has_call_for_quote && min_price == null) {
    priceDisplay = <span className="text-amber-400 font-medium">Call</span>;
  } else if (has_call_for_quote) {
    const minFee = min_price || 0;
    const maxFee = max_price || min_price || 0;
    priceDisplay = minFee === maxFee ? (
      <span><span className="text-teal-400 font-semibold">${minFee}</span> <span className="text-amber-400 text-xs">/ Call</span></span>
    ) : (
      <span><span className="text-teal-400 font-semibold">${minFee}+</span> <span className="text-amber-400 text-xs">/ Call</span></span>
    );
  } else {
    const minFee = min_price || 75;
    const maxFee = max_price || min_price || 75;
    priceDisplay = <span className="text-teal-400 font-semibold">{minFee === maxFee ? `$${minFee}` : `$${minFee} - $${maxFee}`}</span>;
  }

  // Summarize coverage - use filtered_coverage_areas (searched location) if available, otherwise all coverage_areas
  const relevantCoverage = filtered_coverage_areas?.length > 0 ? filtered_coverage_areas : coverage_areas;
  const coverageCount = relevantCoverage?.length || 0;
  const hasStatewide = relevantCoverage?.some(a => a.type === 'state');
  
  // Build descriptive coverage text
  let coverageText;
  if (hasStatewide) {
    coverageText = 'Statewide';
  } else if (coverageCount === 1) {
    const area = relevantCoverage[0];
    if (area.type === 'county') {
      coverageText = `${area.county} County`;
    } else if (area.type === 'city') {
      coverageText = area.city;
    } else if (area.type === 'zip') {
      coverageText = `${area.zip_codes?.length || 0} ZIP${area.zip_codes?.length !== 1 ? 's' : ''}`;
    } else {
      coverageText = '1 area';
    }
  } else {
    coverageText = `${coverageCount} area${coverageCount !== 1 ? 's' : ''}`;
  }

  const hasLicense = verifications?.licensed?.status === 'verified';
  const hasInsurance = verifications?.insured?.status === 'verified';

  // Format distance display
  const distanceDisplay = distance_miles != null ? `${distance_miles} mi` : '—';
  
  // Build profile URL with location params for distance calculation
  const buildProfileUrl = (withQuote = false) => {
    const params = new URLSearchParams();
    if (searchFilters?.zip_code) params.set('from_zip', searchFilters.zip_code);
    if (searchFilters?.city) params.set('from_city', searchFilters.city);
    if (searchFilters?.state) params.set('from_state', searchFilters.state);
    if (withQuote) params.set('quote', 'true');
    const queryString = params.toString();
    return `/servers/${id}${queryString ? '?' + queryString : ''}`;
  };

  return (
    <tr className={`border-b border-slate-200 dark:border-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800/20 ${selected ? 'bg-teal-500/5' : ''} ${is_founding_member ? 'founding-member-row' : ''}`}>
      <td className="py-4">
        <button onClick={onToggle} className="p-1">
          {selected ? <CheckSquare className="h-5 w-5 text-teal-400" /> : <Square className="h-5 w-5 text-slate-600 hover:text-slate-400" />}
        </button>
      </td>
      <td className="py-4">
        <ServioScoreMini score={trust_score?.overall || 0} />
      </td>
      <td className="py-4">
        <Link to={buildProfileUrl()} className="font-medium text-foreground hover:text-teal-400">{display_name}</Link>
        {company_name && <p className="text-xs text-muted-foreground">{company_name}</p>}
      </td>
      <td className="py-4 text-sm text-slate-400">{coverageText}</td>
      <td className="py-4 text-center">
        {distance_miles != null ? (
          <span className="inline-flex items-center gap-1 text-sm text-slate-400">
            <Navigation className="h-3 w-3 text-cyan-400" />
            <span className="font-mono">{distanceDisplay}</span>
          </span>
        ) : (
          <span className="text-slate-600">—</span>
        )}
      </td>
      <td className="py-4 text-right">
        {priceDisplay}
      </td>
      <td className="py-4 text-center">
        {response_guarantee_hours && (
          <span className="inline-flex items-center gap-1 text-sm text-slate-400">
            <Clock className="h-4 w-4 text-purple-400" />{response_guarantee_hours}h
          </span>
        )}
      </td>
      <td className="py-4">
        <VerificationBadges verifications={verifications} isFoundingMember={is_founding_member} stateCertifications={state_certifications} />
      </td>
      <td className="py-4">
        <div className="flex items-center gap-2 justify-end">
          <Link 
            to={buildProfileUrl()}
            className="inline-flex items-center justify-center h-8 px-3 text-xs font-medium rounded-md border border-slate-400 dark:border-slate-700 bg-slate-50 dark:bg-transparent text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
          >Profile</Link>
          <Button asChild size="sm" className="h-8 btn-accent">
            <Link to={buildProfileUrl(true)}>Quote</Link>
          </Button>
        </div>
      </td>
    </tr>
  );
};

// Mobile card component for search results
const ServerMobileCard = ({ server, selected, onToggle, searchFilters }) => {
  const { id, display_name, company_name, trust_score, coverage_areas, filtered_coverage_areas, verifications, response_guarantee_hours, is_founding_member, distance_miles, additional_services, min_price, max_price, has_call_for_quote } = server;

  // Determine price display
  let priceDisplay;
  if (has_call_for_quote && min_price == null) {
    priceDisplay = <span className="text-amber-400 font-medium">Call for Quote</span>;
  } else if (has_call_for_quote) {
    const minFee = min_price || 0;
    priceDisplay = <span><span className="text-teal-400 font-semibold">${minFee}+</span></span>;
  } else {
    const minFee = min_price || 75;
    const maxFee = max_price || min_price || 75;
    priceDisplay = <span className="text-teal-400 font-semibold">{minFee === maxFee ? `$${minFee}` : `$${minFee} - $${maxFee}`}</span>;
  }

  // Summarize coverage - use filtered_coverage_areas (searched location) if available
  const relevantCoverage = filtered_coverage_areas?.length > 0 ? filtered_coverage_areas : coverage_areas;
  const coverageCount = relevantCoverage?.length || 0;
  const hasStatewide = relevantCoverage?.some(a => a.type === 'state');
  
  // Build descriptive coverage text
  let coverageText;
  if (hasStatewide) {
    coverageText = 'Statewide';
  } else if (coverageCount === 1) {
    const area = relevantCoverage[0];
    if (area.type === 'county') {
      coverageText = `${area.county} County`;
    } else if (area.type === 'city') {
      coverageText = area.city;
    } else if (area.type === 'zip') {
      coverageText = `${area.zip_codes?.length || 0} ZIP${area.zip_codes?.length !== 1 ? 's' : ''}`;
    } else {
      coverageText = '1 area';
    }
  } else {
    coverageText = `${coverageCount} area${coverageCount !== 1 ? 's' : ''}`;
  }

  const hasLicense = verifications?.licensed?.status === 'verified';
  const hasInsurance = verifications?.insured?.status === 'verified';
  const hasBond = verifications?.bonded?.status === 'verified';

  // Build profile URL
  const buildProfileUrl = (withQuote = false) => {
    const params = new URLSearchParams();
    if (searchFilters?.zip_code) params.set('from_zip', searchFilters.zip_code);
    if (searchFilters?.city) params.set('from_city', searchFilters.city);
    if (searchFilters?.state) params.set('from_state', searchFilters.state);
    if (withQuote) params.set('quote', 'true');
    const queryString = params.toString();
    return `/servers/${id}${queryString ? '?' + queryString : ''}`;
  };

  return (
    <div className={`card-base p-4 ${selected ? 'ring-2 ring-teal-500/50' : ''} ${is_founding_member ? 'founding-member-card' : ''}`}>
      {/* Header: Checkbox, Score, Name */}
      <div className="flex items-start gap-3 mb-3">
        <button onClick={onToggle} className="mt-1">
          {selected ? <CheckSquare className="h-5 w-5 text-teal-400" /> : <Square className="h-5 w-5 text-slate-600" />}
        </button>
        
        <ServioScoreMini score={trust_score?.overall || 0} size="lg" />

        <div className="flex-1 min-w-0">
          <Link to={buildProfileUrl()} className="font-semibold text-foreground hover:text-teal-400 block truncate">{display_name}</Link>
          {company_name && <p className="text-xs text-muted-foreground truncate">{company_name}</p>}
          
          {/* Badges */}
          <div className="flex flex-wrap items-center gap-1 mt-1">
            {hasLicense && <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-emerald-500/20 text-emerald-400">Licensed</span>}
            {hasInsurance && <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-blue-500/20 text-blue-400">Insured</span>}
            {hasBond && <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-purple-500/20 text-purple-400">Bonded</span>}
            {is_founding_member && <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-amber-500/20 text-amber-400">Founding</span>}
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-2 text-center py-3 border-t border-b border-slate-800/50 mb-3">
        <div>
          <p className="text-xs text-slate-500 mb-1">Coverage</p>
          <p className="text-sm text-slate-300">{coverageText}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1">Distance</p>
          <p className="text-sm text-slate-300">{distance_miles != null ? `${distance_miles} mi` : '—'}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1">Starting</p>
          <p className="text-sm">{priceDisplay}</p>
        </div>
      </div>

      {/* Response time if available */}
      {response_guarantee_hours && (
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
          <Clock className="h-4 w-4 text-purple-400" />
          <span>{response_guarantee_hours}h response guarantee</span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Link 
          to={buildProfileUrl()}
          className="flex-1 inline-flex items-center justify-center h-10 px-4 text-sm font-medium rounded-md border border-slate-400 dark:border-slate-700 bg-slate-50 dark:bg-transparent text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
        >View Profile</Link>
        <Button asChild className="flex-1 btn-accent">
          <Link to={buildProfileUrl(true)}>Request Quote</Link>
        </Button>
      </div>
    </div>
  );
};

// Verification badges with styled tooltips
const VerificationBadges = ({ verifications, isFoundingMember, stateCertifications = [] }) => {
  const hasInsurance = verifications?.insured?.status === 'verified';
  const hasBond = verifications?.bonded?.status === 'verified';
  
  // Build state certification badges with abbreviated labels based on requirement type
  // certification → C, license → L, registration → R
  const stateBadges = stateCertifications.map(cert => {
    const badgeText = cert.badge_text || '';
    // Extract the type from badge_text (e.g., "TX Certified" → "Certified", "CA Registered" → "Registered")
    let abbrev = 'C';
    let hoverText = badgeText;
    if (badgeText.toLowerCase().includes('licensed')) {
      abbrev = 'L';
    } else if (badgeText.toLowerCase().includes('registered')) {
      abbrev = 'R';
    } else if (badgeText.toLowerCase().includes('certified')) {
      abbrev = 'C';
    }
    return { abbrev, hoverText, state: cert.state };
  });
  
  if (!hasInsurance && !hasBond && !isFoundingMember && stateBadges.length === 0) return null;
  
  return (
    <div className="flex items-center gap-1">
      {/* State-specific certification badges (L/C/R based on state requirements) */}
      {stateBadges.map((badge, idx) => (
        <span 
          key={idx} 
          className={`px-1.5 py-0.5 text-xs font-bold rounded border ${
            badge.abbrev === 'L' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
            badge.abbrev === 'C' ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' :
            'bg-violet-500/20 text-violet-400 border-violet-500/30'
          }`}
          title={badge.hoverText}
        >
          {badge.abbrev}
        </span>
      ))}
      {hasInsurance && (
        <span className="px-1.5 py-0.5 text-xs font-bold rounded border bg-blue-500/20 text-blue-400 border-blue-500/30" title="Insured">
          I
        </span>
      )}
      {hasBond && (
        <span className="px-1.5 py-0.5 text-xs font-bold rounded border bg-purple-500/20 text-purple-400 border-purple-500/30" title="Bonded">
          B
        </span>
      )}
      {isFoundingMember && (
        <span className="px-1.5 py-0.5 text-xs font-bold rounded border bg-amber-500/20 text-amber-400 border-amber-500/30" title="Founding Member">
          FM
        </span>
      )}
    </div>
  );
};
// Verification legend with toggle
const VerificationLegend = () => {
  const [showLegend, setShowLegend] = React.useState(false);
  
  return (
    <div className="relative inline-block">
      <button 
        onClick={() => setShowLegend(!showLegend)}
        className="p-1 text-slate-500 hover:text-slate-300 transition-colors flex items-center gap-1"
        title="Status Key"
      >
        <Shield className="h-4 w-4" />
      </button>
      {showLegend && (
        <div className="absolute z-50 right-0 mt-1 p-3 bg-slate-900 border border-slate-700 rounded-lg shadow-xl min-w-[200px]">
          <p className="text-xs font-semibold text-white mb-2 uppercase tracking-wider">Status Key</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs">
              <span className="px-1.5 py-0.5 font-bold rounded border bg-emerald-500/20 text-emerald-400 border-emerald-500/30">L</span>
              <span className="text-slate-400">Licensed (State)</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="px-1.5 py-0.5 font-bold rounded border bg-cyan-500/20 text-cyan-400 border-cyan-500/30">C</span>
              <span className="text-slate-400">Certified (State)</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="px-1.5 py-0.5 font-bold rounded border bg-violet-500/20 text-violet-400 border-violet-500/30">R</span>
              <span className="text-slate-400">Registered (State)</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="px-1.5 py-0.5 font-bold rounded border bg-blue-500/20 text-blue-400 border-blue-500/30">I</span>
              <span className="text-slate-400">Insured</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="px-1.5 py-0.5 font-bold rounded border bg-purple-500/20 text-purple-400 border-purple-500/30">B</span>
              <span className="text-slate-400">Bonded</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="px-1.5 py-0.5 font-bold rounded border bg-amber-500/20 text-amber-400 border-amber-500/30">FM</span>
              <span className="text-slate-400">Founding Member</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchPage;
